import React, { PureComponent } from 'react';
import { browserHistory } from 'react-router';
import { isEmpty } from 'lodash';
import Bugsnag from '@bugsnag/js';
import Indicator from './components/Loader';
import Notify from './components/Notify';
import {
  getOnlineOffline,
  getSyncDate,
  getUser,
  storeThemeColor,
  storeThemeColorNumber,
  getThemeColor,
  getThemeColorNumber,
  _getPreferredColorScheme
} from './common/globals';
import Modals from './components/Models/Modals';
import { onMessageListener } from './helpers/firebase';
import { productDb } from './utils/db';
import withOnline from './common/withOnline';
import { store } from './redux/store.js';
import {
  notificationsSet,
  prevRouteSet
} from './redux/actions.js';
import { getNotifications } from './utils/utils.js';
import withNotifications from './common/withNotifications.js';
import withThemeColor from './common/withThemeColor.js';
import withThemeDetectColor from './common/withThemeDetectColor.js';
import withThemeNumber from './common/withThemeNumber.js';
import withBrowserDetect from './common/withBrowserDetect.js';

class App extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      loadDb: false,
    };

    this.interval = null;
  }

  componentDidMount() {
    const { online } = this.props;
    const user = getUser();
    // this._configureScanditSDK();

    try {
      navigator.serviceWorker.addEventListener('message', () => {
        this._addNewReceivedNotification();
      });

      store.dispatch(prevRouteSet(window.location.pathname));
      this.unlisten = browserHistory.listen(() => {
        store.dispatch(prevRouteSet(window.location.pathname));
      });

      if (!online || !user.sessionToken || window.location.pathname === '/notifications') return;
      getNotifications({ status: 'unread' }).then((notificationsResponse) => {
        if (notificationsResponse) {
          store.dispatch(notificationsSet({
            totalUnread: notificationsResponse.totalUnread,
            data: notificationsResponse.notifications
          }));
        }
      });

      // Set Color
      this._setPreferredColorScheme();
    } catch (e) {
      Bugsnag.notify(e);
    }
  }

  componentDidUpdate() {
    try {
      onMessageListener().then(() => {
        this._addNewReceivedNotification();
      });

      // Detect Theme Color
      this.detectThemeColor();
      this.interval = setInterval(this.detectThemeColor, 10000);

      // Detect Browser
      this.detectBrowser();
    } catch (e) {
      Bugsnag.notify(e);
    }
  }

  componentWillUnmount() {
    this.unlisten();

    if (this.interval) {
      clearInterval(this.interval);
    }
  }

  _addNewReceivedNotification = () => {
    const { notifications, online } = this.props;
    const user = getUser();
    if (!online || !user.sessionToken) return;
    getNotifications({ status: 'unread' }).then((notificationsResponse) => {
      if (notificationsResponse) {
        store.dispatch(notificationsSet({
          totalUnread: notificationsResponse.totalUnread,
          data: [notificationsResponse.notifications[0]].concat(notifications.data)
        }));
      }
    });
  }

  _setPreferredColorScheme = async () => {
    try {
      const {
        setThemeColor, setThemeColorNumberSet, themeColorNumber, themeDetectColor
      } = this.props;

      const color = await getThemeColor();
      const colorNumber = await getThemeColorNumber();

      if (color === 'null' && colorNumber === 'null') {
        if (themeColorNumber === '') {
          setThemeColorNumberSet(2);
          await storeThemeColorNumber(2);
          if (themeDetectColor !== '') {
            setThemeColor(themeDetectColor);
            await storeThemeColor(themeDetectColor);
          } else {
            setThemeColor('light');
            await storeThemeColor('light');
          }
        }
      } else {
        setThemeColorNumberSet(colorNumber);
        await storeThemeColor(color);
        setThemeColor(color);
      }

      _getPreferredColorScheme();
    } catch (e) {
      Bugsnag.notify(e);
    }
  };

  detectThemeColor = async () => {
    try {
      // console.log('detectThemeColor');
      const {
        setThemeDetectColor, themeDetectColor, themeColorNumber, setThemeColor
      } = this.props;
      // console.log(themeColorNumber);
      // console.log(themeDetectColor);

      if (themeDetectColor !== '') {
        if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
          if (themeDetectColor !== 'dark') {
            setThemeDetectColor('dark');

            if (Number(themeColorNumber) === 2) {
              await storeThemeColor('dark');
              setThemeColor('dark');
              _getPreferredColorScheme();
            }
          }
        } else {
          if (themeDetectColor !== 'light') {
            setThemeDetectColor('light');

            if (Number(themeColorNumber) === 2) {
              await storeThemeColor('light');
              setThemeColor('light');
              _getPreferredColorScheme();
            }
          }
        }
      } else {
        setThemeDetectColor('light');
      }
    } catch (e) {
      Bugsnag.notify(e);
    }
  };

  detectBrowser = async () => {
    try {
      const {
        setBrowserDetect, browserDetect
      } = this.props;

      let browserName = 'Chrome';
      const { userAgent } = navigator;
      if (userAgent.includes('Chrome') && !userAgent.includes('Edg') && !userAgent.includes('OPR')) {
        browserName = 'Chrome';
      } else if (userAgent.includes('Edg')) {
        browserName = 'Edg';
      } else if (userAgent.includes('Firefox')) {
        browserName = 'Firefox';
      } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
        browserName = 'Safari';
      } else if (userAgent.includes('OPR') || userAgent.includes('Opera')) {
        browserName = 'OPR';
      }

      if (browserDetect !== '') {
        if (browserDetect !== browserName) {
          setBrowserDetect(browserName);
        }
      } else {
        setBrowserDetect(browserName);
      }
    } catch (e) {
      Bugsnag.notify(e);
    }
  };

  async UNSAFE_componentWillMount() {
    const { online } = this.props;
    getOnlineOffline();

    window.reactRouteChange();
    productDb().then(() => {
      this.setState({ loadDb: true });
    });

    browserHistory.listen(() => {
      window.reactRouteChange();
    });

    // Detect Theme Color
    await this.detectThemeColor();

    // Set Color
    await this._setPreferredColorScheme();

    // Detect Browser
    await this.detectBrowser();

    const user = await getUser();
    const syncDate = await getSyncDate();
    if (!isEmpty(user) && user.id != null && user.sessionToken !== null) {
      // Execute after 24hrs of login time once redirect to manage-cases screen
      const now = new Date();
      const diffInHours = (now - new Date(syncDate)) / 3600000;
      if (online && diffInHours > 24) {
        browserHistory.push('/sync-data');
      }
    }
  }

  render() {
    // console.log('app', this.props);
    const { component } = this.props;
    const { loadDb } = this.state;
    return (
      <div id="app">
        <div className="main-wrapper main-wrapper-1">
          <Modals />
          <Notify />
          <Indicator />
          {loadDb && component}
        </div>
      </div>
    );
  }
}

export default withNotifications(
  withThemeDetectColor(
    withThemeColor(
      withThemeNumber(
        withOnline(
          withBrowserDetect(
            App
          )
        )
      )
    )
  )
);
